import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUploadConsent } from '../../hooks/useUploadConsent';
import { useGetStarted } from '../../hooks/useGetStarted';
import { useStore } from '../../../../store';
import { takeSnapshot } from '../../../../services/snapshot.service';
import { VIEWS_MAP } from '../../../../router/constants';
import { trackMixPanel } from '../../../../services/mixpanel.service';
import { useFormHook } from '../../../../hooks/providers/useFormHooks';
import { accountantScheme, accountantSchemeDefaultValue } from './schemes/accountantScheme';
import { adminScheme, adminSchemeDefaultValue } from './schemes/adminScheme';
import { InviteFlowFields } from '../../consts';
import { baseScheme, baseSchemeDefaultValue } from './schemes/baseScheme';
import { RoleType } from '../../../../types';
import { InviteFormTermsType } from './components/InviteTermsCheckbox';

interface InviteFlowTexts {
  title: string;
  subtitle: string;
  formTitle: string;
}

export const useTeamMemberDetailsPageLogic = () => {
  const { t } = useTranslation('inviteEN');
  const { t: errorsT } = useTranslation('errorsEN');
  const [isTakingSnapShot, setIsTakingSnapshot] = useState<boolean>(false);

  const [isTempBlock, setIsTempBlock] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const roleType: RoleType = (searchParams.get('role') as RoleType) || RoleType.ACCOUNTANT;
  const { customerId } = useStore((state) => state.user);
  const { mutateAsync: asyncGetStarted, isLoading } = useGetStarted();
  const { mutateAsync: asyncUploadConsent, isLoading: isUploadingConsent } = useUploadConsent();
  const navigate = useNavigate();
  const [selectedTermsDoc, setSelectedTermsDoc] = useState<InviteFormTermsType | null>(null);
  const inviteFlowTexts = ((): InviteFlowTexts => {
    switch (roleType) {
      case RoleType.ACCOUNTANT:
        return {
          title: t('INVITE_FORM.TITLE.ACCOUNTANT'),
          subtitle: t('INVITE_FORM.DESCRIPTION.ACCOUNTANT'),
          formTitle: t('INVITE_FORM.INPUTS_TITLE.ACCOUNTANT'),
        };
      case RoleType.ADMIN:
        return {
          title: t('INVITE_FORM.TITLE.ADMIN'),
          subtitle: t('INVITE_FORM.DESCRIPTION.ADMIN'),
          formTitle: t('INVITE_FORM.INPUTS_TITLE.ADMIN'),
        };
      default:
        return {
          title: '',
          subtitle: '',
          formTitle: '',
        };
    }
  })();
  const schemeByRole = () => {
    switch (roleType) {
      case RoleType.ACCOUNTANT:
        return accountantScheme;
      case RoleType.ADMIN:
        return adminScheme;
      default:
        return baseScheme;
    }
  };
  const defaultValuesByRole = () => {
    switch (roleType) {
      case RoleType.ACCOUNTANT:
        return accountantSchemeDefaultValue;
      case RoleType.ADMIN:
        return adminSchemeDefaultValue;
      default:
        return baseSchemeDefaultValue;
    }
  };
  const formMethods = useFormHook({
    schemaTitle: 'UpdateVendorFormWrapper',
    formSchema: schemeByRole(),
    formDefaultValues: defaultValuesByRole(),
  });
  const collectAccountantData = () => {
    const formData = formMethods.getValues();
    return {
      email: formData[InviteFlowFields.EMAIL],
    };
  };

  const collectAdminData = () => {
    const formData = formMethods.getValues();
    return {
      email: formData[InviteFlowFields.EMAIL],
      firstName: formData[InviteFlowFields.FIRST_NAME]?.trim(),
      lastName: formData[InviteFlowFields.LAST_NAME]?.trim(),
    };
  };
  const firstName = formMethods.watch(InviteFlowFields.FIRST_NAME);
  const lastName = formMethods.watch(InviteFlowFields.LAST_NAME);
  const inviteName = firstName && lastName ? `${firstName} ${lastName}` : null;
  const inviteEmail = formMethods.watch(InviteFlowFields.EMAIL);

  const collectInputs = () => {
    switch (roleType) {
      case RoleType.ACCOUNTANT:
        return collectAccountantData();
      case RoleType.ADMIN:
        return collectAdminData();
      default:
        return {};
    }
  };
  const { isValid } = useFormState({ control: formMethods.control });
  const onSubmit = async () => {
    setIsTakingSnapshot(true);
    const consentVersions = [{ name: 'general', type: 'AUTHORIZED_VIEWER_TERM', version: '1.0' }];
    const consentData = await takeSnapshot('snapShotSelector');
    setIsTakingSnapshot(false);

    if (consentData instanceof FormData) {
      consentData.append(
        'consents',
        new Blob([JSON.stringify(consentVersions)], {
          type: 'application/json',
        }),
      );
      await asyncUploadConsent({ customerId, consentData });
    }
    try {
      const collectedData = collectInputs();
      const res = await asyncGetStarted({ inputs: collectedData, customerId, type: roleType });
      const mfaRoute = `/${VIEWS_MAP.INVITE_ACCOUNTANT.INVITE}/${VIEWS_MAP.INVITE_ACCOUNTANT.VERIFY_PHONE}?role=${roleType}`;
      navigate(mfaRoute, {
        state: { mfaData: res.data },
      });
    } catch (e: any) {
      const error = e.response.data?.errors[0];
      if (error.code === 4026) {
        setIsTempBlock(true);
      } else {
        const errMsg = error?.localizedMessage;
        if (errMsg) {
          formMethods.setError(InviteFlowFields.EMAIL, { message: errMsg });
        }
      }
    }
  };

  useEffect(() => {
    trackMixPanel('Page view', { pageName: 'provide viewer email' });
  }, []);

  const onTermsClick = (type: InviteFormTermsType) => {
    switch (type) {
      case InviteFormTermsType.ACCOUNTANT_VIEWER_TERMS:
        setSelectedTermsDoc(type);
        break;

      case InviteFormTermsType.ADMIN_CORPORATE_RESOLUTION:
        if (inviteName) {
          setSelectedTermsDoc(InviteFormTermsType.ADMIN_CORPORATE_RESOLUTION);
        } else {
          if (!firstName) {
            formMethods.setError(InviteFlowFields.FIRST_NAME, { message: errorsT('FIRST_NAME_REQUIRED') });
          }
          if (!lastName) {
            formMethods.setError(InviteFlowFields.LAST_NAME, { message: errorsT('LAST_NAME_REQUIRED') });
          }
        }
        break;

      case InviteFormTermsType.APPOINT_ADMIN:
        if (inviteEmail && firstName && lastName) {
          setSelectedTermsDoc(InviteFormTermsType.APPOINT_ADMIN);
          return;
        }
        if (!inviteEmail) {
          formMethods.setError(InviteFlowFields.EMAIL, { message: errorsT('EMAIL_REQUIRED') });
        }
        if (!firstName) {
          formMethods.setError(InviteFlowFields.FIRST_NAME, { message: errorsT('FIRST_NAME_REQUIRED') });
        }
        if (!lastName) {
          formMethods.setError(InviteFlowFields.LAST_NAME, { message: errorsT('LAST_NAME_REQUIRED') });
        }

        break;

      default:
        setSelectedTermsDoc(type);
        break;
    }
  };

  const hideTermsModal = () => {
    setSelectedTermsDoc(null);
  };

  return {
    onSubmit,
    isValid,
    isLoading: isLoading || isUploadingConsent || isTakingSnapShot,
    formMethods,
    hideTermsModal,
    selectedTermsDoc,
    setIsTempBlock,
    onTermsClick,
    isTempBlock,
    roleType,
    inviteFlowTexts,
    inviteName,
    inviteEmail,
  };
};
